<template>
  <USelectMenu
    v-model="currentValue"
    :items="items"
    label-key="_label"
    :by="compareItems"
    :placeholder="$t('resource.select')"
    :filter-fields="['name', 'displayName']"
    :class="fieldClass"
  >
    <template #default="{ modelValue: selected }">
      <div v-if="selected" class="flex items-center">
        <div class="pr-2">
          <p class="leading-none text-sm text-gray-800">
            {{ selected.name || selected.displayName }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 leading-none mt-1">
            {{ getTypeLabel(selected) }}
          </p>
        </div>
      </div>
    </template>

    <template #item="{ item }">
      <div class="flex items-center my-1">
        <o-avatar v-if="item instanceof Employee" :items="[item]" class="hidden md:flex pr-2" />
        <div class="leading-none text-sm">
          <p>{{ item.name || item.displayName }}</p>
          <p class="text-sm text-gray-600 mt-1 dark:text-gray-300">
            {{ getTypeLabel(item) }}
          </p>
        </div>
      </div>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import { OAvatar } from '@/components/elements'

import { Employee } from '~/_api/models/employee'
import { ResourceType } from '~/_api/models/resourceType'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  modelValue?: any
  fieldClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  fieldClass: 'text-sm h-10',
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
}>()

const { $api, $i18n } = useNuxtApp()

const resourceTypes = ref<ResourceType[]>([])

const currentValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const employeeStore = useEmployeeStore()
const resources = computed(() => employeeStore.employeesTypeResource)
const employees = computed(() => employeeStore.employeesTypeEmployee)

function compareItems(a: any, b: any): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (a instanceof ResourceType && b instanceof ResourceType) return a.idResourceType === b.idResourceType
  if (a instanceof Employee && b instanceof Employee) return a.idEmployee === b.idEmployee
  return false
}

function getTypeLabel(item: any): string {
  if (item instanceof ResourceType) return $i18n.t('general.group') as string
  if (item instanceof Employee && item.isResourceType) return $i18n.t('general.resource') as string
  return $i18n.t('general.employee') as string
}

const items = computed(() => {
  const groups = resourceTypes.value
  const resourceEmps = resources.value
  const employeeEmps = employees.value

  return [
    [{ type: 'label' as const, label: $i18n.t('components.service.elements.resource.groups') as string }, ...groups],
    [{ type: 'label' as const, label: $i18n.t('general.resources') as string }, ...resourceEmps],
    [{ type: 'label' as const, label: $i18n.t('general.employees') as string }, ...employeeEmps],
  ]
})

const fetchResourceTypes = async () => {
  try {
    const response = await $api.resourceType.list()
    resourceTypes.value = response.items
  } catch (e) {
    console.log(e)
  }
}

onMounted(() => {
  fetchResourceTypes()
  employeeStore.sync()
})
</script>
